import { jt, t } from "ttag";

import { useMessenger } from "@/app/hooks/use-messenger";
import { LogoLoader } from "@/shared/components";
import type { ModalProps } from "@/shared/models/legacy-types";
import { Button, Dialog, PillsIcon } from "@/shared/ui";
import { useMarkMedicationAsReceived } from "../hooks";
import type { RefillRequest } from "../models";

interface MarkMedicationAsReceivedModalProps extends ModalProps {
  medicationRequestId: RefillRequest["id"];
}

export const MarkMedicationAsReceivedModal = ({
  show,
  onClose,
  medicationRequestId,
}: MarkMedicationAsReceivedModalProps) => {
  const markMedicationAsReceivedMutation = useMarkMedicationAsReceived();

  const handleConfirm = () => {
    markMedicationAsReceivedMutation.mutate(medicationRequestId);
  };

  const {
    actions: { showMessenger },
  } = useMessenger();

  const handleContactSupport = () => {
    onClose();
    showMessenger();
  };

  const supportButton = (
    <button
      onClick={handleContactSupport}
      key="support-button"
      className="font-semibold underline"
      type="button"
    >
      {t`support`}
    </button>
  );
  const daysStrong = <strong key="days-strong">{t`more than 12 days`}</strong>;
  const backStrong = <strong key="back-strong">{t`Back`}</strong>;

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <Dialog.Header icon={PillsIcon}>
          <Dialog.Title>{t`Mark as received`}</Dialog.Title>
          <Dialog.Description>
            {t`We'll mark this medication as received and update your order status.`}
            <br />
            {jt`If it's been ${daysStrong} since you placed your order and you haven't received it yet, please reach out to ${supportButton}. If you're still within that timeframe, just hit ${backStrong}.`}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <Button size="lg" variant="secondary" onClick={onClose}>
            {t`Back`}
          </Button>
          <Button size="lg" onClick={handleConfirm}>
            {t`Yes, I've received it`}
          </Button>
        </Dialog.Footer>

        <LogoLoader
          show={markMedicationAsReceivedMutation.isPending}
          fullscreen
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};
